import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { BrandCheckbox } from "component/shared/brand-checkbox";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { styles } from "./style";

/** Horizontal gap between pills, matching the flex gap in style.ts. */
const PILL_GAP = 8;
/** Room held for the "+N more" pill before it has rendered once (Tag: 16px padding, 14px text). */
const OVERFLOW_PILL_FALLBACK = 80;

export interface RecipientOption {
	/** Stable key. Fixed recipients use a string ("accountOwner"); custom ones use their id. */
	id: string;
	label: string;
	/** Shown after the label in the dropdown, e.g. the Account Owner's resolved email. */
	detail?: string;
}

interface OwnProps {
	options: RecipientOption[];
	selectedIds: string[];
	onChange: (selectedIds: string[]) => void;
	/** Renders the "+ Add recipients..." row at the foot of the dropdown when provided. */
	onAddRecipient?: () => void;
	addRecipientLabel?: string;
	placeholder?: string;
	disabled?: boolean;
	/**
	 * Renders a delete affordance on deletable options. Without it there is no way to reach the
	 * delete-recipient confirmation, which is how the flow was previously unreachable.
	 */
	onDeleteRecipient?: (id: string) => void;
	/** Which options the delete affordance applies to — the client's own addresses, not the fixed ones. */
	deletableIds?: string[];
}

interface Props extends OwnProps, WithStyles<typeof styles> {}

const Component: React.FC<Props> = ({
	options,
	selectedIds,
	onChange,
	onAddRecipient,
	addRecipientLabel = "Add recipients or lead forwarding email",
	placeholder = "Select recipients",
	disabled = false,
	onDeleteRecipient,
	deletableIds,
	classes = {},
}) => {
	const [open, setOpen] = useState(false);
	const anchorRef = useRef<HTMLDivElement | null>(null);
	const pillsRef = useRef<HTMLDivElement | null>(null);
	const [visibleCount, setVisibleCount] = useState(selectedIds.length);

	const selected = selectedIds
		.map((id) => options.find((option) => option.id === id))
		.filter(Boolean) as RecipientOption[];

	// Render all pills, then hide the ones that overflow.
	useLayoutEffect(() => {
		const container = pillsRef.current;
		if (!container) {
			return;
		}
		const measure = () => {
			const available = container.clientWidth;
			if (!available) {
				return;
			}
			// The placeholder span is also a child here, so filter to pills only.
			const pills = (Array.from(container.children) as HTMLElement[]).filter((child) =>
				child.dataset.pill === "true",
			);
			if (!pills.length) {
				return;
			}
			// Measure every pill at its natural width by un-hiding the hidden ones for the read. The
			// previous cached-width approach left a pill that first rendered hidden at 0, so rows with
			// identical selections could collapse differently (MAX-4339). Restored before paint.
			const displays = pills.map((pill) => pill.style.display);
			pills.forEach((pill) => {
				pill.style.display = "";
			});
			const widths = pills.map((pill) => pill.offsetWidth + PILL_GAP);
			pills.forEach((pill, index) => {
				pill.style.display = displays[index];
			});

			const total = widths.reduce((sum, width) => sum + width, 0) - PILL_GAP;

			// "+N more" sits inline right after the last visible pill (MAX-4339), inside this container,
			// so when not everything fits its width has to be held back from the space the pills get.
			const overflow = container.querySelector<HTMLElement>("[data-overflow-pill]");
			const overflowWidth = (overflow?.offsetWidth || OVERFLOW_PILL_FALLBACK) + PILL_GAP;
			const room = available - overflowWidth;
			let used = 0;
			let fits = 0;
			for (const width of widths) {
				if (used + width - PILL_GAP > room) {
					break;
				}
				used += width;
				fits += 1;
			}
			const next = total <= available ? pills.length : Math.max(1, fits);
			setVisibleCount((current) => (current === next ? current : next));
		};
		measure();
		// rAF the write: a ResizeObserver fires during layout, and writing state there loops.
		let frame = 0;
		const observer = new ResizeObserver(() => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(measure);
		});
		observer.observe(container);
		return () => {
			cancelAnimationFrame(frame);
			observer.disconnect();
		};
	}, [selectedIds, options]);

	useEffect(() => {
		if (disabled) {
			setOpen(false);
		}
	}, [disabled]);

	const toggle = (id: string) => {
		onChange(
			selectedIds.includes(id)
				? selectedIds.filter((selectedId) => selectedId !== id)
				: [...selectedIds, id],
		);
	};

	const remove = (event: React.MouseEvent, id: string) => {
		// Without this the click bubbles to the field and reopens the menu.
		event.stopPropagation();
		onChange(selectedIds.filter((selectedId) => selectedId !== id));
	};

	const hidden = selected.length - visibleCount;

	return (
		<Mui.Box className={classes.root}>
			<Mui.Box
				ref={anchorRef}
				className={`${classes.field} ${open ? classes.fieldOpen : ""}`}
				onClick={() => !disabled && setOpen((wasOpen) => !wasOpen)}
			>
				<Mui.Box ref={pillsRef} className={classes.pills}>
					{selected.length === 0 && (
						<span className={classes.placeholder}>{placeholder}</span>
					)}
					{selected.map((option, index) => (
						<span
							key={option.id}
							className={classes.pill}
							data-pill="true"
							style={index >= visibleCount ? { display: "none" } : undefined}
						>
							{option.label}
							<span className={classes.pillRemove} onClick={(e) => remove(e, option.id)}>
								<Icons.X size={16} />
							</span>
						</span>
					))}
					{hidden > 0 && (
						<span className={`${classes.pill} ${classes.overflowPill}`} data-overflow-pill="true">
							+{hidden} more
						</span>
					)}
				</Mui.Box>
				<span className={`${classes.chevron} ${open ? classes.chevronOpen : ""}`}>
					<Icons.ChevronDown size={16} />
				</span>
			</Mui.Box>

			<Mui.Popper
				open={open}
				anchorEl={anchorRef.current}
				placement="bottom-start"
				disablePortal
				style={{ zIndex: 1300, width: anchorRef.current?.clientWidth }}
			>
				<Mui.ClickAwayListener onClickAway={() => setOpen(false)}>
					<Mui.Paper elevation={3} className={classes.menu}>
						<Mui.List dense disablePadding>
							{options.map((option) => (
								<Mui.ListItem button key={option.id} className={classes.option} onClick={() => toggle(option.id)}>
									<BrandCheckbox
										checked={selectedIds.includes(option.id)}
										tabIndex={-1}
										disableRipple
									/>
									<Mui.ListItemText
										className={classes.optionText}
										primary={
											<span className={classes.optionLabel}>
												{option.label}
												{option.detail ? ` (${option.detail})` : ""}
											</span>
										}
									/>
									{onDeleteRecipient && (deletableIds ?? []).includes(option.id) && (
										<Mui.IconButton
											size="small"
											aria-label={`Delete ${option.label}`}
											onClick={(event) => {
												// The row toggles selection; deleting must not also tick the box.
												event.stopPropagation();
												setOpen(false);
												onDeleteRecipient(option.id);
											}}
										>
											<Icons.Trash2 size={16} />
										</Mui.IconButton>
									)}
								</Mui.ListItem>
							))}
						</Mui.List>
						{onAddRecipient && (
							<Mui.Box
								className={classes.addRow}
								onClick={() => {
									setOpen(false);
									onAddRecipient();
								}}
							>
								<Icons.Plus size={16} />
								{addRecipientLabel}
							</Mui.Box>
						)}
					</Mui.Paper>
				</Mui.ClickAwayListener>
			</Mui.Popper>
		</Mui.Box>
	);
};

export const RecipientMultiSelect = withStyles(Component, styles);
